import { Lock } from "@/icons";
import {
  cn,
  DEFAULT_THUMBNAIL,
  getContentOverlay,
  getDateFromUTCString,
  getUrlFromFilename,
  itemRequiresApp,
} from "@/utils";
import { ImgHTMLAttributes, useMemo, useState } from "react";
import { Skeleton } from "@/components/Loading/Skeleton.tsx";
import { Collection, Video } from "@/api";
import { useAppProperties } from "@/hooks";
import { useTranslate, useUser } from "@/providers/context";

interface ThumbnailProps extends ImgHTMLAttributes<HTMLImageElement> {
  thumbnail: string; // filename in S3 or HTTP URL
  contentItem?: Partial<Video> | Partial<Collection>; // provides overlay
  isDripLocked?: boolean;
  isInHeroCarousel?: boolean;
  className?: string;
}

export const ContentThumbnail = ({
  thumbnail,
  contentItem,
  isDripLocked,
  isInHeroCarousel,
  className,
  ...props
}: ThumbnailProps) => {
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { data: properties } = useAppProperties();
  const { isVideoUnlocked, isCollectionUnlocked } = useUser();
  const { translate, formatDetailedDatetime } = useTranslate();

  const locked = useMemo(() => {
    if (!contentItem || isDripLocked) {
      return false;
    }
    if ("ProductId" in contentItem) {
      return !isVideoUnlocked(contentItem as Video);
    }

    return !isCollectionUnlocked(contentItem as Collection);
  }, [isCollectionUnlocked, isVideoUnlocked, contentItem, isDripLocked]);

  const url = thumbnail ? getUrlFromFilename(thumbnail) : DEFAULT_THUMBNAIL;

  const overlayNode = getContentOverlay(
    contentItem,
    properties ?? {},
    translate,
    formatDetailedDatetime,
  );
  const overlayTop = useMemo(() => {
    if (!contentItem) return false;
    const ambiguousItem = contentItem as Video & Collection;
    const isAppOnly = itemRequiresApp(ambiguousItem as Video | Collection);

    if (isAppOnly) {
      return true;
    }

    if (ambiguousItem?.VideoId) {
      // Move live video overlays to the top
      return !!ambiguousItem?.Properties?.LiveEventStartDate;
    }
    return false;
  }, [contentItem]);

  const overlayClassName = useMemo(() => {
    if (!contentItem) return false;
    const ambiguousItem = contentItem as Video & Collection;
    if (ambiguousItem?.VideoId) {
      const liveStartDate = getDateFromUTCString(
        ambiguousItem?.Properties?.LiveEventStartDate,
      );
      const started = !!liveStartDate && liveStartDate < new Date();
      if (started) {
        return "bg-surface-live";
      }
    }
    return "";
  }, [contentItem]);

  return (
    <div
      className={cn(["relative aspect-video overflow-hidden", className])}
      {...props}
    >
      <img
        src={isError ? DEFAULT_THUMBNAIL : url}
        onLoad={() => setLoading(false)}
        onError={() => {
          setIsError(true);
          setLoading(false);
        }}
        className={cn("h-full w-full object-cover", {
          "h-0 w-0 opacity-0": loading,
        })}
        alt="thumbnail"
      />
      {loading ? (
        <Skeleton className="h-full w-full" />
      ) : (
        <>
          {isDripLocked && (
            <div
              className={cn(
                "absolute inset-0 flex justify-end bg-surface-overlay-50",
                isInHeroCarousel ? "p-3" : "p-2",
              )}
            >
              <div
                className={cn(
                  "h-fit bg-surface-secondary py-0.5 text-item-primary",
                  isInHeroCarousel
                    ? "caption-semibold rounded-md px-1.5 leading-5"
                    : "subtitle-semibold rounded-[4px] px-1 leading-4",
                )}
              >
                {translate("Coming Soon")}
              </div>
            </div>
          )}
          {(overlayNode || locked) && (
            <div
              className={cn(
                "absolute  right-2 flex gap-1 text-white",
                overlayTop ? "top-2" : "bottom-2",
              )}
            >
              {overlayNode && (
                <div
                  className={cn(
                    "subtitle rounded-[4px] px-1 py-0.5",
                    overlayClassName || "bg-surface-overlay-80",
                  )}
                >
                  {overlayNode}
                </div>
              )}
              {locked && (
                <div className="rounded-[4px] bg-surface-overlay-80 p-[3px]">
                  <Lock className="h-3 w-3" />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
